import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../services/ProjectService/project';
import { AuthService } from '../../services/AuthService/auth';
import { ProjectResponse, ProjectStatus } from '../../models/project.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { AddParticipantDialog } from '../../dialogs/add-participant-dialog/add-participant-dialog';
import { ConfirmDialog } from '../../dialogs/confirm-dialog/confirm-dialog';

interface StatusAction {
  status: ProjectStatus;
  label: string;
  icon: string;
  danger?: boolean;
}

// Mirrors ProjectService.requireLegalTransition in the backend: ACTIVE <-> SUSPENDED either way,
// either may be cancelled, and CANCELLED is the one-way door. Offering anything else here would
// just surface a 400 PROJECT_STATUS_TERMINAL.
const STATUS_TRANSITIONS: Record<ProjectStatus, StatusAction[]> = {
  ACTIVE: [
    { status: 'SUSPENDED', label: 'Pause project', icon: 'pause_circle' },
    { status: 'CANCELLED', label: 'Cancel project', icon: 'cancel', danger: true },
  ],
  SUSPENDED: [
    { status: 'ACTIVE', label: 'Resume project', icon: 'play_circle' },
    { status: 'CANCELLED', label: 'Cancel project', icon: 'cancel', danger: true },
  ],
  CANCELLED: [],
};

@Component({
  selector: 'app-project-detail',
  imports: [CommonModule, RouterLink, StatusBadge, ErrorBanner, EmptyState, MatMenuModule, MatIconModule],
  templateUrl: './project-detail.html',
  styleUrl: './project-detail.scss',
})
export class ProjectDetail implements OnInit {
  projectId = '';
  project = signal<ProjectResponse | null>(null);

  isLoading = signal(false);
  errorMessage = signal('');
  notFound = signal(false);

  reactivatingCompanyId = signal<string | null>(null);
  isUpdatingStatus = signal(false);
  isLeaving = signal(false);

  // Kept in sync with the transition duration on .detail-page--leaving in the stylesheet.
  private readonly leaveAnimationMs = 220;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private auth: AuthService,
    private dialog: MatDialog
  ) {}

  goBack(): void {
    if (this.isLeaving()) {
      return;
    }
    this.isLeaving.set(true);
    setTimeout(() => this.router.navigateByUrl('/projects'), this.leaveAnimationMs);
  }

  get canRaiseExportRequest(): boolean {
    const role = this.auth.getSession()?.role;
    return role === 'COMPANY_ADMIN' || role === 'COMPANY_USER' || role === 'SUPERADMIN';
  }

  get canManageParticipants(): boolean {
    return this.auth.getSession()?.role === 'SUPERADMIN';
  }

  // Same roles the backend's PATCH /api/projects/{id} write gate allows (minus API_CLIENT,
  // which authenticates via API key rather than this UI).
  get canManageStatus(): boolean {
    const role = this.auth.getSession()?.role;
    return role === 'COMPANY_ADMIN' || role === 'COMPANY_USER';
  }

  get statusActions(): StatusAction[] {
    const status = this.project()?.status;
    return status ? STATUS_TRANSITIONS[status] : [];
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.notFound.set(false);

    this.projectService.get(this.projectId).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.project.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        if (err.status === 404) {
          this.notFound.set(true);
          return;
        }
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load project.'));
      },
    });
  }

  openAddParticipantDialog(): void {
    const existingCompanyIds =
      this.project()
        ?.participants.filter((p) => p.status !== 'REMOVED')
        .map((p) => p.companyId) ?? [];
    this.dialog
      .open(AddParticipantDialog, { width: '480px', data: { projectId: this.projectId, existingCompanyIds } })
      .afterClosed()
      .subscribe((added) => {
        if (added) {
          this.load();
        }
      });
  }

  confirmRemoveDialog(companyId: string, companyName: string): void {
    this.dialog
      .open(ConfirmDialog, {
        width: '420px',
        data: {
          title: 'Remove participant?',
          message: `${companyName} will lose access to this project and won't be able to raise or receive export requests on it. You can add them back later.`,
          confirmLabel: 'Remove',
          cancelLabel: 'Cancel',
          danger: true,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.removeParticipant(companyId);
        }
      });
  }

  private removeParticipant(companyId: string): void {
    this.errorMessage.set('');
    this.projectService.removeParticipant(this.projectId, companyId).subscribe({
      next: () => this.load(),
      error: (err: HttpErrorResponse) => {
        this.errorMessage.set(extractErrorMessage(err, 'Failed to remove participant.'));
      },
    });
  }

  reactivate(companyId: string, registrationNo: string): void {
    this.reactivatingCompanyId.set(companyId);
    this.errorMessage.set('');

    this.projectService.addParticipant(this.projectId, { registrationNo }).subscribe({
      next: () => {
        this.reactivatingCompanyId.set(null);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.reactivatingCompanyId.set(null);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to reactivate participant.'));
      },
    });
  }

  transitionStatus(action: StatusAction): void {
    if (!action.danger) {
      this.applyStatus(action.status);
      return;
    }

    this.dialog
      .open(ConfirmDialog, {
        width: '420px',
        data: {
          title: 'Cancel this project?',
          message:
            'Participants will no longer be able to raise new export requests against it, and the status cannot be ' +
            'changed back. Requests already pending can still be accepted or declined. To stop it only temporarily, ' +
            'pause the project instead.',
          confirmLabel: 'Cancel project',
          cancelLabel: 'Keep project',
          danger: true,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.applyStatus(action.status);
        }
      });
  }

  private applyStatus(status: ProjectStatus): void {
    this.isUpdatingStatus.set(true);
    this.errorMessage.set('');

    this.projectService.update(this.projectId, { status }).subscribe({
      next: (res) => {
        this.isUpdatingStatus.set(false);
        this.project.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isUpdatingStatus.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to update project status.'));
      },
    });
  }
}
